import {
  ExternalLocationHubConfig,
  ExternalReturnHubType,
} from "@redotech/account-common/address";
import { ProcessingFeeBasis } from "@redotech/crdb-enums/fulfillment/processing-fee-basis";
import { ProcessingLevel } from "@redotech/crdb-enums/fulfillment/processing-level";
import { findCountryCode } from "@redotech/locale/countries";
import { Currency } from "@redotech/money/currencies";
import {
  DEFAULT_INVENTORY_PUSH_MINIMUM_QUANTITY,
  getInventoryPushMinimumQuantity,
} from "@redotech/redo-inventory-common/inventory-push-buffer";
import {
  defaultLicensePlateTemplate,
  type LicensePlateTemplate,
} from "@redotech/redo-inventory-common/license-plate-template";
import {
  defaultReceivingLabelField,
  ReceivingLabelField,
} from "@redotech/redo-inventory-common/receiving-label";
import { readReturnsCenterId } from "@redotech/redo-return-core-common/returns-center-id";
import {
  GradeAcceptance,
  type GradeMapping,
} from "@redotech/redo-return-settings-common/grade-mapping";
import {
  DEFAULT_PROCESSING_BILLING_CONFIG,
  type ProcessingBillingConfig,
  type ReturnsCenterPricing,
} from "@redotech/redo-return-settings-common/processing-billing-config";
import type { ReceivingOutcome } from "@redotech/redo-return-settings-common/receiving-outcome-config";
import {
  DEFAULT_RETURNS_PROCESSING_COST,
  type redoManagedReturnsSettings,
} from "@redotech/redo-return-settings-common/redo-managed-returns-settings";
import { RemitMode } from "@redotech/redo-return-settings-common/remit-mode";
import type { ReturnsCenterValues } from "@redotech/redo-return-settings-common/returns-center-setting";
import { ReturnProcessingLevel } from "@redotech/redo-return-settings-primitives-common/return-processing-level";
import {
  ConsolidationShipToAddress,
  ReturnsCenterConfig,
} from "@redotech/redo-return-settings-primitives-common/returns-center-config";
import { Team } from "@redotech/redo-team-common/team";
import { returnsCenterIdFormat } from "@redotech/redo-warehouse-id";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { numberValidator } from "@redotech/ui/validators";
import { deepIntersectionEquals } from "@redotech/util/equal";

export interface ReturnsCenterConfigFormValue {
  returnsCenterId: string;
  consolidationScheduleDays: string;
  shipToName: string;
  shipToStreet1: string;
  shipToStreet2: string;
  shipToCity: string;
  shipToState: string;
  shipToZip: string;
  shipToCountry: string;
  refulfillment: boolean;
  processingFee: string;
  processingFeeBasis: ProcessingFeeBasis | null;
  processingCurrency?: Currency | null;
  externalProcessingFee: string;
  externalProcessingFeeBasis: ProcessingFeeBasis | null;
}

export interface ExternalLocationHubFormConfig {
  provider: ExternalReturnHubType | "";
  locationId: string;
}

function toRawCenterId(returnsCenterId: string): string {
  return returnsCenterIdFormat.test(returnsCenterId)
    ? returnsCenterIdFormat.read(returnsCenterId).toString()
    : returnsCenterId;
}

function formatFee(value: number | null): string {
  return value == null ? "" : value.toFixed(2);
}

function parseFee(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") {
    return null;
  }
  const fee = Number(trimmed);
  return isNaN(fee) ? null : fee;
}

function formatCoverageSharePercent(value: number | undefined | null): string {
  if (value == null || value === 0) {
    return "";
  }
  return Number((value * 100).toFixed(6)).toString();
}

function parseCoverageSharePercent(value: string): number {
  const numberValue = Number(value);
  return isNaN(numberValue) ? 0 : numberValue / 100;
}

function coverageSharePercentValidator(value: {
  processingCoverageSharePercent: string;
  consolidationCoverageSharePercent: string;
}): string[] {
  const total =
    (Number(value.processingCoverageSharePercent) || 0) +
    (Number(value.consolidationCoverageSharePercent) || 0);
  if (total > 100) {
    return ["Total coverage percentage cannot exceed 100%."];
  }
  return [];
}

function receivingOutcomeRequiredValidator(value: {
  returnProcessingLevel: ProcessingLevel;
  receivingOutcome: ReceivingOutcome | null;
}): string[] {
  if (
    value.returnProcessingLevel === ProcessingLevel.Receiving &&
    value.receivingOutcome == null
  ) {
    return ["Receiving requires an outcome."];
  }
  return [];
}

/** A center at receiving needs an outcome of its own or from the account-wide setting. */
function centerReceivingOutcomeRequiredValidator(value: {
  receivingOutcome: ReceivingOutcome | null;
  availableReturnsCenters: readonly { returnsCenterId: string }[];
  returnsCenterProcessingLevels: ReturnsCenterValues<ProcessingLevel>;
  returnsCenterReceivingOutcomes: ReturnsCenterValues<ReceivingOutcome>;
}): string[] {
  // A center removed in this save loses its settings, so it can't block the save.
  const teamCenterIds = new Set(
    value.availableReturnsCenters.map(({ returnsCenterId }) =>
      readReturnsCenterId(returnsCenterId)?.toString(),
    ),
  );
  return Object.entries(value.returnsCenterProcessingLevels).flatMap(
    ([returnsCenterId, level]) =>
      teamCenterIds.has(readReturnsCenterId(returnsCenterId)?.toString()) &&
      level === ProcessingLevel.Receiving &&
      (value.returnsCenterReceivingOutcomes[returnsCenterId] ??
        value.receivingOutcome) == null
        ? [`Returns center ${returnsCenterId}: receiving requires an outcome.`]
        : [],
  );
}

/** Changing the processing currency needs an explicit fee (0 is allowed); amounts are never converted. */
export function processingCurrencyFeeErrors(value: {
  processingCost: string;
  processingCurrency: Currency;
  savedProcessingCurrency: Currency;
}): string[] {
  return value.processingCost.trim() === "" &&
    value.processingCurrency !== value.savedProcessingCurrency
    ? [
        `Enter a processing fee in ${value.processingCurrency} (0 is allowed) before changing the processing currency.`,
      ]
    : [];
}

export function managedReturnsSettingsValidator(value: {
  processingCost: string;
  processingCurrency: Currency;
  savedProcessingCurrency: Currency;
  processingCoverageSharePercent: string;
  consolidationCoverageSharePercent: string;
  returnProcessingLevel: ProcessingLevel;
  receivingOutcome: ReceivingOutcome | null;
  availableReturnsCenters: readonly { returnsCenterId: string }[];
  returnsCenterProcessingLevels: ReturnsCenterValues<ProcessingLevel>;
  returnsCenterReceivingOutcomes: ReturnsCenterValues<ReceivingOutcome>;
}): string[] {
  return [
    ...processingCurrencyFeeErrors(value),
    ...coverageSharePercentValidator(value),
    ...receivingOutcomeRequiredValidator(value),
    ...centerReceivingOutcomeRequiredValidator(value),
  ];
}

/** Each center's receiving outcome, kept only for a center that ends up at receiving; the rest save cleared. */
export function centerReceivingOutcomesForTheirLevels(value: {
  returnProcessingLevel: ProcessingLevel;
  returnsCenterProcessingLevels: ReturnsCenterValues<ProcessingLevel>;
  returnsCenterReceivingOutcomes: ReturnsCenterValues<ReceivingOutcome>;
}): ReturnsCenterValues<ReceivingOutcome> {
  const levels = new Map(
    Object.entries(value.returnsCenterProcessingLevels).flatMap(
      ([rawId, level]) => {
        const id = readReturnsCenterId(rawId);
        return id === null ? [] : [[id, level] as const];
      },
    ),
  );
  const levelOf = (rawId: string): ProcessingLevel => {
    const id = readReturnsCenterId(rawId);
    return (id === null ? null : levels.get(id)) ?? value.returnProcessingLevel;
  };
  return Object.fromEntries(
    Object.entries(value.returnsCenterReceivingOutcomes).map(
      ([rawId, outcome]) => [
        rawId,
        levelOf(rawId) === ProcessingLevel.Receiving ? outcome : null,
      ],
    ),
  );
}

function validateInventoryPushMinimumQuantity(value: string): string[] {
  const trimmed = value.trim();
  const parsed = Number(trimmed);
  if (!/^\d+$/.test(trimmed) || !Number.isSafeInteger(parsed)) {
    return ["Enter a whole number of 0 or more."];
  }
  return [];
}

const INT4_MAX = 2_147_483_647;

/** Mirrors the CRDB schedule_days int4 + `> 0` CHECK; empty or 0 means no
 * automated consolidation (0 is stored as off). */
export function returnsCenterScheduleDaysErrors(value: string): string[] {
  const trimmed = value.trim();
  if (trimmed === "") {
    return [];
  }
  if (trimmed.startsWith("-")) {
    return ["Days between shipments cannot be negative."];
  }
  if (!/^\d+$/.test(trimmed)) {
    return ["Days between shipments must be a whole number."];
  }
  if (Number(trimmed) > INT4_MAX) {
    return ["Days between shipments exceeds the maximum allowed value."];
  }
  return [];
}

/** Mirrors the CRDB `processing_fee >= 0` CHECK and decimal(19, 8) range. */
export function processingFeeErrors(value: string): string[] {
  const trimmed = value.trim();
  if (trimmed === "") {
    return [];
  }
  const fee = Number(trimmed);
  if (isNaN(fee) || fee < 0) {
    return ["Processing fee must be 0 or more."];
  }
  if (fee >= 1e11) {
    return ["Processing fee exceeds the maximum allowed value."];
  }
  return [];
}

export function externalProcessingFeeErrors(value: string): string[] {
  const trimmed = value.trim();
  if (trimmed === "") {
    return [];
  }
  const fee = Number(trimmed);
  if (isNaN(fee) || fee <= 0) {
    return [
      "External processing fee must be greater than 0. Leave it blank to use standard pricing.",
    ];
  }
  if (fee >= 1e11) {
    return ["External processing fee exceeds the maximum allowed value."];
  }
  return [];
}

export function returnsCenterFeeBasisErrors(
  center: Pick<
    ReturnsCenterConfigFormValue,
    "processingFee" | "processingFeeBasis"
  > &
    Partial<
      Pick<
        ReturnsCenterConfigFormValue,
        "externalProcessingFee" | "externalProcessingFeeBasis"
      >
    >,
): string[] {
  if (center.processingFee.trim() !== "" && center.processingFeeBasis == null) {
    return ["A processing fee override requires a fee basis."];
  }
  if (
    (center.externalProcessingFee ?? "").trim() !== "" &&
    center.externalProcessingFeeBasis == null
  ) {
    return ["An external returns fee override requires a fee basis."];
  }
  return [];
}

export const availableReturnsCentersInput = input<
  ReturnsCenterConfigFormValue[]
>({
  equal: deepIntersectionEquals,
  validator: (centers) =>
    centers.flatMap((center) =>
      [
        ...returnsCenterScheduleDaysErrors(center.consolidationScheduleDays),
        ...processingFeeErrors(center.processingFee),
        ...externalProcessingFeeErrors(center.externalProcessingFee),
        ...returnsCenterFeeBasisErrors(center),
      ].map((error) => `${center.returnsCenterId}: ${error}`),
    ),
});

export const redoManagedReturnsSettingsForm = groupInput(
  {
    enabled: input<boolean>(),
    processingCost: input<string>({ validator: processingFeeErrors }),
    processingFeeBasis: input<ProcessingFeeBasis>(),
    externalProcessingFee: input<string>({
      validator: externalProcessingFeeErrors,
    }),
    externalProcessingFeeBasis: input<ProcessingFeeBasis>(),
    processingCurrency: input<Currency>(),
    savedProcessingCurrency: input<Currency>(),
    processingCoverageSharePercent: input<string>({
      validator: numberValidator({ min: 0, max: 100 }),
    }),
    consolidationCoverageSharePercent: input<string>({
      validator: numberValidator({ min: 0, max: 100 }),
    }),
    returnProcessingLevel: input<ProcessingLevel>(),
    receivingOutcome: input<ReceivingOutcome | null>(),
    remitMode: input<RemitMode>(),
    verificationPassOutcome: input<GradeAcceptance>(),
    verificationFailOutcome: input<GradeAcceptance>(),
    inventoryPushMinimumQuantity: input<string>({
      validator: validateInventoryPushMinimumQuantity,
    }),
    receivingLabelEnabled: input<boolean>(),
    licensePlateTemplate: input<LicensePlateTemplate>(),
    receivingLabelEncodedField: input<ReceivingLabelField>(),
    externalLocationHubMappings: input<
      Record<string, ExternalLocationHubFormConfig>
    >({ equal: deepIntersectionEquals }),
    availableReturnsCenters: availableReturnsCentersInput,
    gradeMappings: input<GradeMapping[]>({ equal: deepIntersectionEquals }),
    returnsCenterProcessingLevels: input<ReturnsCenterValues<ProcessingLevel>>({
      equal: deepIntersectionEquals,
    }),
    returnsCenterReceivingOutcomes: input<
      ReturnsCenterValues<ReceivingOutcome>
    >({ equal: deepIntersectionEquals }),
    customGradesEnabled: input<boolean>(),
  },
  { validator: managedReturnsSettingsValidator },
);

export type RedoManagedReturnsSettingsForm = InputSchema.Input<
  typeof redoManagedReturnsSettingsForm
>;
export type RedoManagedReturnsSettingsValue = InputSchema.Value<
  typeof redoManagedReturnsSettingsForm
>;

export const redoManagedReturnsSettingsDefault: RedoManagedReturnsSettingsValue =
  {
    enabled: false,
    processingCurrency: Currency.USD,
    savedProcessingCurrency: Currency.USD,
    processingCost: "",
    processingFeeBasis: DEFAULT_PROCESSING_BILLING_CONFIG.global.basis,
    externalProcessingFee: "",
    externalProcessingFeeBasis: ProcessingFeeBasis.PerReturn,
    processingCoverageSharePercent: "",
    consolidationCoverageSharePercent: "",
    returnProcessingLevel: ProcessingLevel.Verification,
    receivingOutcome: null,
    remitMode: RemitMode.AUTOMATIC,
    verificationPassOutcome: GradeAcceptance.ACCEPT,
    verificationFailOutcome: GradeAcceptance.FLAG_FOR_REVIEW,
    inventoryPushMinimumQuantity: `${DEFAULT_INVENTORY_PUSH_MINIMUM_QUANTITY}`,
    receivingLabelEnabled: false,
    licensePlateTemplate: defaultLicensePlateTemplate,
    receivingLabelEncodedField: defaultReceivingLabelField,
    externalLocationHubMappings: {},
    availableReturnsCenters: [],
    gradeMappings: [],
    returnsCenterProcessingLevels: {},
    returnsCenterReceivingOutcomes: {},
    customGradesEnabled: false,
  };

export interface ManagedReturnsCenterProcessing {
  returnsCenterProcessingLevels: ReturnsCenterValues<ProcessingLevel>;
  returnsCenterReceivingOutcomes: ReturnsCenterValues<ReceivingOutcome>;
}

/** Grade settings read from CRDB; the team-document copies are vestigial. */
export interface ManagedReturnsGradeSettings {
  gradeMappings: GradeMapping[];
  remitMode: RemitMode;
}

export const redoManagedReturnsTeamToFormValue = (
  team: {
    settings: {
      redoManagedReturns?: redoManagedReturnsSettings;
      returnItemDisposition?: Team["settings"]["returnItemDisposition"];
    };
  },
  billing: ProcessingBillingConfig | null,
  returnProcessingLevel?: ProcessingLevel,
  receivingOutcome?: ReceivingOutcome | null,
  gradeSettings?: ManagedReturnsGradeSettings | null,
  centerProcessing?: ManagedReturnsCenterProcessing | null,
): RedoManagedReturnsSettingsValue => {
  const storedMappings =
    team.settings.redoManagedReturns?.externalLocationHubMappings;
  const formMappings: Record<string, ExternalLocationHubFormConfig> = {};
  if (storedMappings) {
    for (const [key, value] of Object.entries(storedMappings)) {
      if (!value) {
        continue;
      }
      // DB stores raw numeric keys; convert to formatted for form display
      const formattedKey =
        key && !returnsCenterIdFormat.test(key)
          ? returnsCenterIdFormat.write(BigInt(key))
          : key;
      formMappings[formattedKey] = {
        provider: value.provider,
        locationId: value.locationId ?? "",
      };
    }
  }

  const storedCenters =
    team.settings.redoManagedReturns?.availableReturnsCenters ?? [];
  const formCenters: ReturnsCenterConfigFormValue[] = storedCenters.map(
    (config) => {
      const formattedCenterId =
        config.returnsCenterId &&
        !returnsCenterIdFormat.test(config.returnsCenterId)
          ? returnsCenterIdFormat.write(BigInt(config.returnsCenterId))
          : config.returnsCenterId;
      const pricing: ReturnsCenterPricing | undefined =
        billing?.centers[toRawCenterId(formattedCenterId)];
      return {
        returnsCenterId: formattedCenterId,
        consolidationScheduleDays:
          config.consolidationScheduleDays?.toString() ?? "",
        shipToName: config.shipToAddress?.name ?? "",
        shipToStreet1: config.shipToAddress?.street1 ?? "",
        shipToStreet2: config.shipToAddress?.street2 ?? "",
        shipToCity: config.shipToAddress?.city ?? "",
        shipToState: config.shipToAddress?.state ?? "",
        shipToZip: config.shipToAddress?.zip ?? "",
        shipToCountry:
          findCountryCode(config.shipToAddress?.country ?? "") ??
          config.shipToAddress?.country ??
          "",
        refulfillment: config.refulfillment ?? false,
        processingFee: formatFee(pricing?.fee ?? null),
        processingFeeBasis: pricing?.basis ?? null,
        processingCurrency: pricing ? (pricing.currency ?? null) : undefined,
        externalProcessingFee: formatFee(pricing?.externalFee ?? null),
        externalProcessingFeeBasis: pricing?.externalBasis ?? null,
      };
    },
  );

  return {
    enabled: team.settings.redoManagedReturns?.enabled ?? false,
    processingCurrency: billing?.global.currency ?? Currency.USD,
    savedProcessingCurrency: billing?.global.currency ?? Currency.USD,
    processingCost: billing ? formatFee(billing.global.fee) : "",
    processingFeeBasis:
      billing?.global.basis ?? DEFAULT_PROCESSING_BILLING_CONFIG.global.basis,
    externalProcessingFee: formatFee(billing?.global.externalFee ?? null),
    externalProcessingFeeBasis:
      billing?.global.externalBasis ?? ProcessingFeeBasis.PerReturn,
    processingCoverageSharePercent: formatCoverageSharePercent(
      team.settings.redoManagedReturns?.processingCoverageSharePercent,
    ),
    consolidationCoverageSharePercent: formatCoverageSharePercent(
      team.settings.redoManagedReturns?.consolidationCoverageSharePercent,
    ),
    returnProcessingLevel:
      returnProcessingLevel ??
      (team.settings.redoManagedReturns?.returnProcessingLevel ===
      ReturnProcessingLevel.GRADING
        ? ProcessingLevel.Grading
        : ProcessingLevel.Verification),
    receivingOutcome: receivingOutcome ?? null,
    remitMode:
      gradeSettings?.remitMode ??
      team.settings.redoManagedReturns?.remitMode ??
      RemitMode.AUTOMATIC,
    verificationPassOutcome:
      team.settings.redoManagedReturns?.verificationPassOutcome ??
      GradeAcceptance.ACCEPT,
    verificationFailOutcome:
      team.settings.redoManagedReturns?.verificationFailOutcome ??
      GradeAcceptance.FLAG_FOR_REVIEW,
    inventoryPushMinimumQuantity: getInventoryPushMinimumQuantity(
      team.settings.redoManagedReturns,
    ).toString(),
    receivingLabelEnabled:
      team.settings.redoManagedReturns?.receivingLabel?.enabled ?? false,
    licensePlateTemplate:
      team.settings.redoManagedReturns?.licensePlateTemplate ??
      defaultLicensePlateTemplate,
    receivingLabelEncodedField:
      team.settings.redoManagedReturns?.receivingLabel?.encodedField ??
      defaultReceivingLabelField,
    externalLocationHubMappings: formMappings,
    availableReturnsCenters: formCenters,
    gradeMappings: [
      ...(gradeSettings?.gradeMappings ??
        team.settings.redoManagedReturns?.gradeMappings ??
        []),
    ],
    returnsCenterProcessingLevels: {
      ...centerProcessing?.returnsCenterProcessingLevels,
    },
    returnsCenterReceivingOutcomes: {
      ...centerProcessing?.returnsCenterReceivingOutcomes,
    },
    customGradesEnabled: team.settings.returnItemDisposition?.enabled ?? false,
  };
};

export const redoManagedReturnsFormValueToTeam = (
  redoManagedReturnsSettings: RedoManagedReturnsSettingsValue,
  stored: redoManagedReturnsSettings | undefined,
): Team["settings"]["redoManagedReturns"] => {
  const mappings = redoManagedReturnsSettings.externalLocationHubMappings;
  const hubMappings: Record<string, ExternalLocationHubConfig> = {};
  if (mappings) {
    for (const [key, value] of Object.entries(mappings)) {
      if (value.provider) {
        // Convert formatted key (rctr_...) back to raw numeric for DB storage
        const rawKey = returnsCenterIdFormat.test(key)
          ? returnsCenterIdFormat.read(key).toString()
          : key;
        hubMappings[rawKey] = {
          provider: value.provider,
          locationId:
            value.locationId && value.locationId.trim().length > 0
              ? value.locationId
              : undefined,
        };
      }
    }
  }

  const storedCenters = new Map(
    (stored?.availableReturnsCenters ?? []).map((center) => [
      toRawCenterId(center.returnsCenterId),
      center,
    ]),
  );

  const formCenters = redoManagedReturnsSettings.availableReturnsCenters ?? [];
  const availableReturnsCenters: ReturnsCenterConfig[] = formCenters.map(
    (c) => {
      const rawCenterId = toRawCenterId(c.returnsCenterId);
      const storedCenter = storedCenters.get(rawCenterId);

      const hasAddress = c.shipToName || c.shipToStreet1;
      const shipToAddress: ConsolidationShipToAddress | undefined = hasAddress
        ? {
            ...storedCenter?.shipToAddress,
            name: c.shipToName,
            street1: c.shipToStreet1,
            street2: c.shipToStreet2 || undefined,
            city: c.shipToCity,
            state: c.shipToState || undefined,
            zip: c.shipToZip,
            country: c.shipToCountry,
          }
        : undefined;

      const days = Number(c.consolidationScheduleDays);

      // Start from the stored center so an unedited one serializes exactly as
      // stored: protected-settings approval compares the centers as JSON, so
      // a different key order alone would ask for approval. This also keeps
      // server-maintained fields like lastConsolidationAt.
      return {
        ...storedCenter,
        returnsCenterId: rawCenterId,
        consolidationScheduleDays:
          c.consolidationScheduleDays === "" || isNaN(days) ? undefined : days,
        shipToAddress,
        // The form reads an unset flag as off; keep it unset until turned on.
        refulfillment:
          c.refulfillment || storedCenter?.refulfillment !== undefined
            ? c.refulfillment
            : undefined,
        processingFee: storedCenter?.processingFee,
      };
    },
  );

  return {
    enabled: redoManagedReturnsSettings.enabled,
    processingFee: stored?.processingFee ?? DEFAULT_RETURNS_PROCESSING_COST,
    processingCost: stored?.processingCost ?? DEFAULT_RETURNS_PROCESSING_COST,
    processingCoverageSharePercent: parseCoverageSharePercent(
      redoManagedReturnsSettings.processingCoverageSharePercent,
    ),
    consolidationCoverageSharePercent: parseCoverageSharePercent(
      redoManagedReturnsSettings.consolidationCoverageSharePercent,
    ),
    returnProcessingLevel:
      stored?.returnProcessingLevel ?? ReturnProcessingLevel.VERIFICATION,
    remitMode: stored?.remitMode,
    verificationPassOutcome:
      redoManagedReturnsSettings.verificationPassOutcome ??
      GradeAcceptance.ACCEPT,
    verificationFailOutcome:
      redoManagedReturnsSettings.verificationFailOutcome ??
      GradeAcceptance.FLAG_FOR_REVIEW,
    inventoryPushMinimumQuantity: parseInventoryPushMinimumQuantity(
      redoManagedReturnsSettings.inventoryPushMinimumQuantity,
    ),
    receivingLabel:
      redoManagedReturnsSettings.receivingLabelEnabled ===
        (stored?.receivingLabel?.enabled ?? false) &&
      redoManagedReturnsSettings.receivingLabelEncodedField ===
        (stored?.receivingLabel?.encodedField ?? defaultReceivingLabelField)
        ? stored?.receivingLabel
        : {
            enabled: redoManagedReturnsSettings.receivingLabelEnabled,
            encodedField: redoManagedReturnsSettings.receivingLabelEncodedField,
          },
    licensePlateTemplate: redoManagedReturnsSettings.licensePlateTemplate,
    externalLocationHubMappings:
      Object.keys(hubMappings).length > 0 ? hubMappings : undefined,
    availableReturnsCenters:
      availableReturnsCenters.length > 0 ? availableReturnsCenters : undefined,
    gradeMappings: stored?.gradeMappings,
  };
};

function parseInventoryPushMinimumQuantity(value: string): number {
  const errors = validateInventoryPushMinimumQuantity(value);
  if (errors.length > 0) {
    return DEFAULT_INVENTORY_PUSH_MINIMUM_QUANTITY;
  }
  return Number(value);
}

export const redoManagedReturnsFormValueToReceivingOutcome = (
  value: RedoManagedReturnsSettingsValue,
): ReceivingOutcome | null => value.receivingOutcome;

export const redoManagedReturnsFormValueToBilling = (
  value: RedoManagedReturnsSettingsValue,
): ProcessingBillingConfig => {
  const centers: Record<string, ReturnsCenterPricing> = {};
  for (const center of value.availableReturnsCenters ?? []) {
    const fee = parseFee(center.processingFee);
    const externalFee = parseFee(center.externalProcessingFee);
    if (
      fee != null ||
      externalFee != null ||
      center.processingCurrency === null
    ) {
      centers[toRawCenterId(center.returnsCenterId)] = {
        fee,
        basis: center.processingFeeBasis,
        currency: center.processingCurrency ?? null,
        externalFee,
        externalBasis:
          center.externalProcessingFee.trim() === ""
            ? null
            : center.externalProcessingFeeBasis,
      };
    }
  }
  return {
    global: {
      currency: value.processingCurrency,
      fee: parseFee(value.processingCost),
      basis: value.processingFeeBasis,
      externalFee: parseFee(value.externalProcessingFee),
      externalBasis:
        value.externalProcessingFee.trim() === ""
          ? null
          : value.externalProcessingFeeBasis,
    },
    centers,
  };
};
